<template>
  <aside class="h-dvh pb-8">
    <ul class="h-full overflow-y-scroll pb-2 pl-2 scrollbar-hide">
      <li
        v-for="(category, index) in displayCategories"
        :key="`category-navigation-tree-item-${category.id}`"
        :data-testid="`root-category-${index}`"
        class="mb-5 mt-2 first:mt-0 last:mb-0"
      >
        <SFCategorySideNavigationItem
          :is-brand-listing="isBrandListing"
          :category="category"
          :is-active="category.id === currentCategory?.id"
          :is-sale="isSaleCategory(category)"
          class="font-semibold text-gray-900"
        />
        <SFCategorySideNavigationChildren
          v-if="category.children?.length"
          :current-category="currentCategory"
          :sub-categories="category.children"
          :is-parent-sale="isSaleCategory(category)"
        />
      </li>
    </ul>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Category } from '@scayle/storefront-nuxt'
import SFCategorySideNavigationChildren from './SFCategorySideNavigationChildren.vue'
import SFCategorySideNavigationItem from './SFCategorySideNavigationItem.vue'
import { isSaleCategory } from '#storefront-product-listing'
import { useRoute } from '#app/composables/router'

const { rootCategories, currentCategory } = defineProps<{
  rootCategories: Category[]
  currentCategory: Category | null
  fetchingCategories: boolean
}>()

const route = useRoute()

const isBrandListing = computed(() => {
  return (
    route.path.includes('/b/') ||
    currentCategory?.slug === 'brands' ||
    currentCategory?.parent?.slug === 'brands'
  )
})

// Flatten all categories to make searching easier
const allCategories = computed(() => {
  const flatten = (categories: Category[]): Category[] => {
    const result: Category[] = []
    for (const category of categories) {
      result.push(category)
      if (category.children?.length) {
        result.push(...flatten(category.children))
      }
    }
    return result
  }
  return flatten(rootCategories)
})

const displayCategories = computed(() => {
  // If no current category, show root categories
  if (!currentCategory) {
    return rootCategories
  }

  // If the current category has children, show those children
  if (currentCategory.childrenIds?.length > 0) {
    return allCategories.value.filter((item) => {
      return currentCategory?.id === item.parentId
    })
  }

  // If the category is a leaf node, show all categories which have the same parent (siblings)
  return allCategories.value.filter((item) => {
    return currentCategory?.parentId === item.parentId
  })
})
</script>
